/**
 * @file appSettings.ts
 *
 * Per-environment app settings. A host plugin overrides them through the
 * `settings` section of its host registration (see features/host/host.ts; e.g.
 * grabcadPlugin.ts hiding the transfer "open in new tab" option), so no core
 * module needs hardcoded knowledge of plugin-specific behaviour. Keyed by env
 * string because plugins self-register unconditionally at import time — the
 * env decides which settings apply.
 */

import { getHost, registerHost } from '../features/host/host.ts';

/** Per-environment behavioural toggles a plugin can override. */
export interface AppSettings {
  /** Whether the transfer modal offers an "open in new tab" option. */
  allowTransferNewTab: boolean;
  /** Whether as-header's language selector accepts user input. Envs that
   *  control language from outside the app (e.g. a `lang` URL param set by
   *  the host) set this false — an in-app switch there wouldn't survive
   *  the host's own reload/relaunch, so offering it would just be confusing. */
  allowLangSelector: boolean;
  /** Whether as-header shows the user-options icon (account details, sign out). */
  showUserOptions: boolean;
  /** Whether links inside shadow roots (e.g. as-embed-content) must be re-issued
   *  through a throwaway light-DOM anchor to open in a new window. Hosts like
   *  GrabCAD's webview only follow real light-DOM `<a target="_blank">` clicks;
   *  window.open and shadow-DOM anchors are dropped. See openViaLightDomAnchor. */
  externalLinksViaLightDomAnchor: boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  allowTransferNewTab: true,
  allowLangSelector: true,
  showUserOptions: true,
  externalLinksViaLightDomAnchor: false,
};

/** Same as `registerHost(env, { settings })`. Repeated calls for the same env
 *  merge shallowly, last value wins. */
export function registerAppSettings(env: string, settings: Partial<AppSettings>): void {
  registerHost(env, { settings });
}

/**
 * Return the effective settings for an env: registered overrides merged over the
 * defaults. An unknown or empty env yields the defaults.
 */
export function getAppSettings(env: string | null | undefined): AppSettings {
  return { ...DEFAULT_SETTINGS, ...getHost(env)?.settings };
}
